import { isPlatformServer } from '@angular/common';
import { Component, DestroyRef, Injectable, Injector, PLATFORM_ID, Type, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { WidgetConfig } from '@frontend/sports/common/client-config-data-access';
import { LoggerFactory } from '@frontend/sports/common/core/feature/logging';
import { toDictionary } from '@frontend/sports/common/core/utils/collection';
import { OptionalNonRootToken } from '@frontend/sports/host-app/sports-product/feature/non-root-token';
import { Widget, WidgetContext, WidgetLocation, WidgetType } from '@frontend/sports/types/components/widget';
import { NativeAppService } from '@frontend/vanilla/core';
import { linkedTransferState } from '@frontend/vanilla/ssr';
import { omit } from 'lodash-es';
import { Subject, Subscription, auditTime, combineLatest, filter, iif, map, of, take, timeout } from 'rxjs';

import { WidgetData, WidgetLoaderService } from '../widget-loader.service';
import { WIDGET_ROUTE_SNAPSHOT_TOKEN } from './widget-route-snapshot.token';
import { WIDGET_SKELETON, WidgetSkeletonDefinition } from './widget-skeleton.model';

export interface RegisteredWidget {
    componentType: Type<WidgetComponent<unknown>>;
    injector?: Injector;
}

@Component({
    template: '',
    host: {
        '[class.hidden]': '_hidden()',
    },
})
export abstract class WidgetComponent<T> {
    readonly _hidden = signal(!inject(NativeAppService).isTerminal);
    // Uses a signal internally
    get hidden() {
        return this._hidden();
    }
    set hidden(hidden) {
        this._hidden.set(hidden);
    }

    // TODO: Using a private field with getter/setter, because we cannot migrate all the usages in the whole codebase for the moment
    private readonly _hasData = signal(true);
    // Uses a signal internally, so it can be used safely in computeds/effects
    get hasData(): boolean {
        return this._hasData();
    }
    set hasData(x: boolean) {
        this._hasData.set(x);
    }

    protected readonly widgetRouteSnapshot = inject(WIDGET_ROUTE_SNAPSHOT_TOKEN);
    private widgetAlreadyReady = false;
    private widgetLoaderService = inject(WidgetLoaderService);
    private readonly widgetConfig = signal<Widget<never> | undefined>(undefined);
    private parentConfig?: Widget<never>;
    private widgetLoadedSubscription$: Subscription;
    private logger = inject(LoggerFactory).getLogger('WidgetComponent');
    private widgetClientConfig = inject(WidgetConfig);

    get parent(): Readonly<Widget<never>> | undefined {
        return this.parentConfig;
    }

    get config(): Readonly<Widget<never>> {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return this.widgetConfig()!;
    }

    protected logEmptyData(): void {
        this.logger.warning(`Empty widget data configured for ${this.config.id}`);
    }

    protected readonly destroyRef = inject(DestroyRef);

    constructor() {
        effect(() => {
            const config = this.widgetConfig();
            const visible = !this._hidden();
            if (config) {
                this.widgetLoaderService.setWidgetVisible(`${this.config.location}-${this.config.id}`, visible);
            }
        });
    }

    onResolve(widget: Widget<T>, parent?: Widget<unknown>, shouldCallOnData = true, isWidgetRefresh = false): void {
        this.widgetConfig.set(omit(widget, 'payload') as Widget<never>);

        if (parent) {
            this.parentConfig = omit(parent, 'payload') as Widget<never>;
        }

        if (shouldCallOnData) {
            if (this.config.location !== WidgetLocation.Right && !isWidgetRefresh) {
                this.widgetAlreadyReady = false;
                this.subscribeWidgetLoaded();
            }

            this.onData?.(widget.payload, isWidgetRefresh);
            if (parent?.payload) {
                this.onParentData?.(parent.payload);
            }
        }
    }

    protected onData?(data?: T, isWidgetRefresh?: boolean): void;

    protected onParentData?(data: unknown): void;

    getContext(): Partial<WidgetContext> {
        return { widgetId: this.config.id };
    }

    protected setWidgetLoaded(): void {
        if (this.widgetAlreadyReady) {
            this.hidden = !this.hasData;

            return;
        }

        //Adding below check to avoid null config from widget constructor on initial load
        if (this.config) {
            this.widgetLoaderService.setWidgetLoaded(`${this.config.location}-${this.config.id}`);
        }
    }

    private subscribeWidgetLoaded(): void {
        this.widgetLoadedSubscription$?.unsubscribe();
        this.widgetLoadedSubscription$ = combineLatest([
            this.widgetLoaderService.widgetLoadedDetails$(this.config.location),
            iif(
                () => [WidgetLocation.Left, WidgetLocation.Center].includes(this.config.location),
                this.widgetLoaderService.allTopLocationWidgetsLoaded$,
                of(true),
            ),
        ])
            .pipe(
                map(([widgetsData, topLocationWidgetsLoaded]) => topLocationWidgetsLoaded && this.checkWidgetReady(widgetsData)),
                filter(Boolean),
                take(1),
                timeout({
                    each: this.widgetClientConfig.orderedRenderingTimeout,
                    with: () => of(false),
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((loadedWithoutTimeout) => {
                if (!loadedWithoutTimeout) {
                    this.logger.warning(
                        `Widget ordered rendering timed out after ${this.widgetClientConfig.orderedRenderingTimeout}ms for ${this.config.id}`,
                    );
                }
                this.widgetAlreadyReady = true;
                this.hidden = !this.hasData;
            });
    }

    private checkWidgetReady = (widgets: WidgetData[]): boolean =>
        widgets.some((x) => x.widgetId === this.config.id && x.isLoaded) && this.checkPreceedingWidgetsReady(widgets);

    private checkPreceedingWidgetsReady = (widgets: WidgetData[]): boolean => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const currentWidgetOrder = widgets.find((widget) => widget.widgetId === this.config.id)!.order;
        const filteredWidgets = widgets.filter((x) => x.order <= currentWidgetOrder);

        return filteredWidgets.every((x) => x.isLoaded);
    };
}

@Injectable({
    providedIn: 'root',
})
export class WidgetRegistryService {
    // inject all candidates that got registered
    private readonly widgetSkeletonConfig = inject<WidgetSkeletonDefinition<unknown>[]>(OptionalNonRootToken(WIDGET_SKELETON), { optional: true });
    // create a dictionary <WidgetType, Component> for faster access
    readonly widgetSkeletons = this.widgetSkeletonConfig
        ? toDictionary(
              this.widgetSkeletonConfig,
              ({ type }) => type.toString(),
              (v) => v,
          )
        : {};

    readonly isServer = isPlatformServer(inject(PLATFORM_ID));

    private readonly widgets = new Map<WidgetType, RegisteredWidget>();
    private readonly lazyWidgets = new Subject<WidgetType>();

    private readonly widgetsCount = linkedTransferState('LAZY_WIDGETS_COUNTS', 0);
    private lazyWidgetCount = 0;

    readonly lazyWidgets$ = this.lazyWidgets.pipe(
        // TODO: evaluate this, only for SSR purpose atm
        // we need to at least debounce it!
        auditTime(15),
        // skip doing anything until everything is registered, and then fire for all
        filter((_data) => (this.isServer ? this.widgets.size > 0 : this.lazyWidgetCount >= this.widgetsCount())),
    );

    register<T>(name: WidgetType, widget: Type<WidgetComponent<T>>): void {
        this.assertNotAlreadyRegistered(name);
        this.widgets.set(name, { componentType: widget });
    }

    registerLazy<T>(name: WidgetType, widget: Type<WidgetComponent<T>>, injector?: Injector): void {
        this.assertNotAlreadyRegistered(name);

        this.widgets.set(name, { componentType: widget, injector });

        if (this.isServer) {
            this.widgetsCount.update((c) => c + 1);
        } else {
            this.lazyWidgetCount++;
        }

        this.lazyWidgets.next(name);
    }

    get(name: WidgetType): RegisteredWidget | null {
        return this.widgets.get(name) ?? null;
    }

    remove(name: WidgetType) {
        this.widgets.delete(name);
    }

    private assertNotAlreadyRegistered(type: WidgetType): void {
        if (this.widgets.has(type)) {
            throw new Error(`Widget with name ${type} is already registered`);
        }
    }
}
